<template>
  <div class="flex flex-col items-center">
    <a
      class="flex cursor-pointer flex-col items-center"
      :href="appURL"
      :aria-label="appData.name"
      :data-testid="`${appData.name}-button`"
      @click.prevent="clickOnApp"
    >
      <div class="flex h-12 w-12 items-center justify-center rounded-full">
        <inline-svg class="h-full w-full" :alt="appData.name" :src="getLogoUrl" />
      </div>
      <span class="image-text mt-2 flex justify-center whitespace-pre-line text-center text-base">
        {{ formattedAppNameWithLineBreaks }}
      </span>
    </a>
  </div>
</template>

<script setup lang="ts">
import type { Application } from '@/types.ts'
import InlineSvg from 'vue-inline-svg'
import { event } from '@/plugins/analytics'
import { computed } from 'vue'
import { auth } from '@/plugins'
import { useCompanies } from '@/plugins/companySelection/companies'
import { useSnackbar } from '@/composables/snackbar'
import { useTranslator } from '@/plugins/translations'

const props = defineProps<{
  appData: Application
}>()

const authenticationClient = auth.useAuthentication()
const { showSnackbar } = useSnackbar()
const { translate } = useTranslator()

const getPublicPathOf = (url: string): string => {
  return new URL(`/${url}`, import.meta?.url).toString()
}

const getLogoUrl = computed(() => {
  return `${getPublicPathOf('appsIcons')}/${props.appData.icon}.svg`
})

const formattedAppNameWithLineBreaks = computed(() => {
  return props.appData.name.replace(/\s+/g, '\n')
})

const { selectedCompany } = useCompanies()

const isSIMA = computed(() => {
  return props.appData?.isSimaDesktop ?? false
})

const buildSIMAUrl = (baseUrl: string, accessToken = authenticationClient.getAccessToken()): string => {
  let path = baseUrl + `//authToken/${accessToken}`

  if (selectedCompany.value) {
    path += `/simaId/${selectedCompany.value.id}`
  }

  return path
}

const buildWebAppUrl = (baseUrl: string): string => {
  if (!selectedCompany.value || !props.appData.useCompanySelection) {
    return baseUrl
  }

  const separator = baseUrl.endsWith('/') ? '' : '/'
  return `${baseUrl}${separator}companies/${selectedCompany.value.id}`
}

const appURL = computed(() => {
  const baseUrl = props.appData.url

  return isSIMA.value ? buildSIMAUrl(baseUrl) : buildWebAppUrl(baseUrl)
})

const clickOnApp = async () => {
  event(`click_${props.appData.name.toLowerCase()}`, { event_category: 'application' })
  let target = '_blank'
  let url = appURL.value

  if (props.appData.isSimaDesktop) {
    target = '_self'

    let accessToken: string | null = null
    let hasValidSession = false
    try {
      accessToken = await authenticationClient.getAccessToken()
      hasValidSession =
        Boolean(accessToken) &&
        (typeof authenticationClient.isLoggedIn !== 'function' ||
          (await authenticationClient.isLoggedIn()))
    } catch {
      hasValidSession = false
    }

    if (!hasValidSession || !accessToken) {
      await authenticationClient.logout()
      if (typeof authenticationClient.signIn === 'function') {
        await authenticationClient.signIn()
      } else {
        await authenticationClient.login()
      }
      return
    }

    showSnackbar(translate('App.SIMADesktop.Opening'))
    url = buildSIMAUrl(props.appData.url, accessToken)
  }

  window.open(url, target)
}
</script>

<style scoped>
a {
  transition: opacity 0.25s ease-in-out;
}

a:hover {
  opacity: 0.6;
  animation: fadeToHover 0.25s ease-in;
}

.image-text {
  color: #484849;
  text-overflow: ellipsis;
  text-align: center;
  cursor: pointer;
}

@keyframes fadeToHover {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}
</style>
